import React, { useCallback, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  conversationReadFilter,
  conversationLastMessageDateFilter,
} from 'constants/forms';
import common from 'constants/common';
import useOutsideClick from 'utils/useOutsideClick';
import { CONVERSATION_TYPES, INITIAL_STATE } from 'store/smsDashboard';
import { useCloseOpenConversation } from 'infrastructure/hooks/smsDashboard/useCloseOpenConversation';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import BaseSelect from 'components/atoms/baseSelect';
import ChatFilterIcon from 'components/atoms/icons/chatFilterIcon';
import BaseButton from 'components/atoms/baseButton';
import ChatFilterActiveIcon from 'components/atoms/icons/chatFilterActiveIcon';
import EditIcon from 'components/atoms/icons/pencilIcon';
import DownloadIcon from 'components/atoms/icons/downloadOutlinedIcon';
import BaseModal from 'components/atoms/base-modal';
import NewConversationButton from 'components/molecules/smsDashboard/newConversationButton';
import { useSmsDashboardConversations } from 'infrastructure/hooks/smsDashboard/useConversations';
import { isNotUndefined } from 'infrastructure/functions';
import XIcon from 'components/atoms/icons/xIcon';
import BaseCheckbox from 'components/atoms/base-checkbox';
import useUser from 'utils/useUser';

import s from './styles.module.scss';

import type { IFilterConversation } from 'infrastructure/interfaces';
import type { RootDispatch, RootState } from 'store';

interface Props {
  selectMode: boolean;
  setSelectMode: (state: boolean) => void;
  checkedConversations: any[];
  setCheckedConversations: React.Dispatch<React.SetStateAction<number[]>>;
  setScrollToMessage: React.Dispatch<React.SetStateAction<any>>;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  searchText: string;
}

const ConversationHeader: React.FC<Props> = ({
  selectMode,
  setSelectMode,
  checkedConversations,
  setCheckedConversations,
  setScrollToMessage,
  setSearchText,
  searchText,
}) => {
  const user = useUser();
  const ref = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState<boolean>(false);

  const { filters, conversationType } = useSelector(
    (state: RootState) => state.smsDashboard,
  );
  const {
    smsDashboard: { updateConversationsFilters },
  } = useDispatch<RootDispatch>();

  const selectTheme = (theme: any) => ({
    ...theme,
    borderRadius: 12,
    colors: {
      ...theme.colors,
      ...common.primaryColors,
    },
  });

  useOutsideClick(ref, () => {
    setIsMenuOpen(false);
  });

  const { closeOpenConversation, closeOpenConversationLoading } =
    useCloseOpenConversation({
      type: conversationType,
      read: filters.read,
      closed: filters.closed,
      lastMessageDate: filters.lastMessageDate,
      searchQuery: filters.searchQuery,
      onSuccess() {
        notificationController.info({
          message: t('smsDashboard.conversation_is_actioned_successfully', {
            action: 'closed',
          }),
        });
        closeSelectMode();
        setIsCloseModalOpen(false);
      },
    });

  const memoizedDate = useMemo(() => {
    const currentDate = new Date();
    return currentDate;
  }, [
    conversationType,
    filters.closed,
    filters.read,
    filters.unenrolled,
    filters.withoutAssignedCcmSpecialist,
    filters.lastMessageDate,
    filters.searchQuery,
  ]);
  const { conversations } = useSmsDashboardConversations({
    cursor: memoizedDate,
    type: conversationType,
    read: filters.read,
    closed: filters.closed,
    assignedToMe: filters.assignedToMe,
    unenrolled: filters.unenrolled,
    withoutAssignedCcmSpecialist: filters.withoutAssignedCcmSpecialist,
    lastMessageDate: filters.lastMessageDate,
    searchQuery: filters.searchQuery,
  });

  const conversationIds =
    conversations
      ?.map((conversation) => conversation?.items.map((item) => item.id))
      ?.flat()
      ?.filter(isNotUndefined) ?? [];

  const conversationCount =
    conversations
      ?.map((conversation) => conversation?.items.map((item) => item.id))
      ?.flat()?.length ?? 0;

  const isAllSelected = checkedConversations.length === conversationIds?.length;
  const onClickSelectAll = () => {
    if (isAllSelected) {
      setCheckedConversations([]);
      return;
    }

    if (checkedConversations.length) {
      const notSelectedYet =
        conversationIds
          ?.filter((item) => !checkedConversations?.includes(item))
          ?.filter(isNotUndefined) ?? [];

      setCheckedConversations((prev) => {
        return [...prev, ...notSelectedYet];
      });
    } else {
      setCheckedConversations(conversationIds);
    }
  };

  const closeSelectMode = () => {
    setSelectMode(false);
    setCheckedConversations([]);
  };

  const filterChanged = async (e: {
    type: string;
    value?: string | boolean;
  }) => {
    if (!e) return;
    updateConversationsFilters({ ...filters, [e.type]: e.value });
  };

  const debouncedSearch = useCallback(
    debounce((data) => {
      filterChanged(data);
    }, 800),
    [filters],
  );

  const filterReset = async () => {
    updateConversationsFilters({ ...INITIAL_STATE.filters });
  };

  const searchApply = async (e: { type: string; value?: string | boolean }) => {
    if (!e) return;
    if (e.type === 'searchQuery') {
      setSearchText(String(e.value));
      debouncedSearch({ type: e.type, value: e.value });
      if (e.value === '') {
        setScrollToMessage({});
      }
    }
  };

  const getFilterValue = (
    options: IFilterConversation[],
    value: string | boolean,
  ) => {
    const res = options.find((element) => element.value === value);
    return { label: res?.label, value: res?.value };
  };

  const isDefaultFilter =
    filters.read === INITIAL_STATE.filters.read &&
    filters.lastMessageDate === INITIAL_STATE.filters.lastMessageDate;

  const filterIcon = isDefaultFilter ? (
    <ChatFilterIcon />
  ) : (
    <ChatFilterActiveIcon />
  );

  const selectButtonTitle =
    filters.closed !== INITIAL_STATE.filters.closed
      ? t('smsDashboard.select_conversations_disabled')
      : t('smsDashboard.select_conversations');

  return (
    <>
      {!selectMode && (
        <div className={s['conversations-header']}>
          <div className={s['conversations-search']}>
            <input
              type="text"
              className={s['conversations-search-input']}
              placeholder={t('smsDashboard.search_number_name')}
              value={searchText}
              onChange={(e) =>
                searchApply({
                  value: e.target.value,
                  type: 'searchQuery',
                })
              }
            />
            {searchText.length ? (
              <span className={s['chat-search-clear']}>
                <button
                  onClick={() => {
                    searchApply({
                      value: '',
                      type: 'searchQuery',
                    });
                  }}
                >
                  <XIcon />
                </button>
              </span>
            ) : (
              <span className={s['chat-search-icon']} />
            )}
          </div>
          <div className={s['conversations-filter']}>
            <button
              className={s['conversations-filter-button']}
              onClick={() => setSelectMode(!selectMode)}
              disabled={
                filters.closed !== INITIAL_STATE.filters.closed ||
                conversationCount === 0
              }
              title={selectButtonTitle}
            >
              <EditIcon />
            </button>
          </div>
          <div className={s['conversations-filter']}>
            <button
              className={s['conversations-filter-button']}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {filterIcon}
            </button>
            {isMenuOpen && (
              <div className={s['conversations-filter-modal']} ref={ref}>
                <div className={s['conversations-filter-header']}>
                  <p>{t('filters.filters')}</p>
                </div>

                <div className={s['conversations-filter-body']}>
                  <div className={s['conversations-filter']}>
                    <span>{t('smsDashboard.message_type')}</span>
                    <BaseSelect
                      options={conversationReadFilter}
                      defaultValue={conversationReadFilter[2]}
                      value={getFilterValue(
                        conversationReadFilter,
                        filters.read,
                      )}
                      theme={(theme) => selectTheme(theme)}
                      classNamePrefix="select-type"
                      onChange={(e) =>
                        filterChanged({
                          type: 'read',
                          value: (e as unknown as { [key: string]: string })
                            .value,
                        })
                      }
                    />
                  </div>
                  <div className={s['conversations-filter']}>
                    <span>{t('smsDashboard.sorting')}</span>
                    <BaseSelect
                      options={conversationLastMessageDateFilter}
                      defaultValue={conversationLastMessageDateFilter[0]}
                      value={getFilterValue(
                        conversationLastMessageDateFilter,
                        filters.lastMessageDate,
                      )}
                      theme={(theme) => selectTheme(theme)}
                      classNamePrefix="select-type"
                      onChange={(e) =>
                        filterChanged({
                          type: 'lastMessageDate',
                          value: (e as unknown as { [key: string]: string })
                            .value,
                        })
                      }
                    />
                  </div>
                  {(user.isAgencyDoctor || user.isPractice) && (
                    <div className={s['conversations-filter']}>
                      <BaseCheckbox
                        placeholder={t('labels.assignedToMe')}
                        value={filters.assignedToMe}
                        onChange={(value) =>
                          filterChanged({
                            type: 'assignedToMe',
                            value,
                          })
                        }
                      />
                    </div>
                  )}
                  {user.isSupervisorRole &&
                    conversationType === CONVERSATION_TYPES.CCM && (
                      <div className={s['conversations-filter']}>
                        <BaseCheckbox
                          placeholder={t('labels.unenrolled')}
                          value={filters.unenrolled}
                          onChange={(value) =>
                            filterChanged({
                              type: 'unenrolled',
                              value,
                            })
                          }
                        />
                      </div>
                    )}
                  {user.isSupervisorRole &&
                    conversationType === CONVERSATION_TYPES.CCM && (
                      <div className={s['conversations-filter']}>
                        <BaseCheckbox
                          placeholder={t('labels.withoutAssignedCcmSpecialist')}
                          value={filters.withoutAssignedCcmSpecialist}
                          onChange={(value) =>
                            filterChanged({
                              type: 'withoutAssignedCcmSpecialist',
                              value,
                            })
                          }
                        />
                      </div>
                    )}
                </div>
                <div className={s['conversations-filter-footer']}>
                  <BaseButton
                    label={t('controls.reset')}
                    type="secondary"
                    onClick={() => {
                      filterReset();
                      setIsMenuOpen(!isMenuOpen);
                    }}
                  />
                  <BaseButton
                    label={t('controls.apply')}
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                  />
                </div>
              </div>
            )}
          </div>
          <NewConversationButton />
        </div>
      )}
      {selectMode && (
        <div
          className={`${s['conversations-header']} ${s['conversations-header-select']}`}
        >
          <div className={s['conversations-search']}>
            {checkedConversations.length} {t('labels.selected')}
          </div>
          <BaseButton
            label={
              isAllSelected
                ? t('controls.deselectAll')
                : t('controls.selectAll')
            }
            type="secondary"
            onClick={onClickSelectAll}
          />
          <BaseButton
            label={t('controls.cancel')}
            type="secondary"
            onClick={closeSelectMode}
          />
          <BaseButton
            label={t('controls.close')}
            type="secondary"
            onClick={() => {
              setIsCloseModalOpen(true);
            }}
            disabled={checkedConversations.length === 0}
            icon={<DownloadIcon />}
          />
          <BaseModal
            title={t('smsDashboard.close_selected_conversations')}
            open={isCloseModalOpen}
            onCancel={() => {
              closeSelectMode();
              setIsCloseModalOpen(false);
            }}
            withFooter={[
              <BaseButton
                dataCy="cancel-button"
                key="cancel"
                type="secondary"
                label={t('controls.cancel')}
                disabled={closeOpenConversationLoading}
                onClick={() => {
                  closeSelectMode();
                  setIsCloseModalOpen(false);
                }}
              />,
              <BaseButton
                dataCy="save-button"
                key="save"
                type="primary"
                label={t('controls.confirm')}
                loading={closeOpenConversationLoading}
                onClick={() => {
                  closeOpenConversation({
                    conversationIds: checkedConversations,
                    close: true,
                  });
                }}
              />,
            ]}
          >
            <p>{t('smsDashboard.close_selected_conversations_message')}</p>
          </BaseModal>
        </div>
      )}
    </>
  );
};

export default ConversationHeader;
